import { moduleCatalog, type ModuleDefinition } from './module-catalog.js';
import {
  permissionCatalog,
  type PermissionDefinition,
  type PermissionKey,
} from './permission-catalog.js';

/// As telas da empresa que podem virar atalho, e a permissão que cada uma exige.
///
/// Mora aqui porque é contrato entre os dois lados: o servidor precisa da permissão para não
/// devolver atalho para tela que a pessoa não alcança, e a web precisa do rótulo para escrever o
/// mesmo nome no menu e no cartão. Escrito duas vezes, um dos dois envelheceria — e o sintoma seria
/// um cartão chamado de um jeito no menu e de outro no início.
///
/// O endereço e o ícone **não** estão aqui: são desenho de tela, e o servidor não tem o que fazer
/// com eles. A web completa cada chave com os dois.
///
/// **Início ficou de fora de propósito.** Ela é a tela que exibe os atalhos; oferecer atalho para
/// si mesma seria um cartão que não leva a lugar nenhum, e contar as visitas dela faria "mais
/// acessados" começar sempre pela tela que a pessoa está olhando.

export type CompanyScreen = {
  key: string;
  label: string;
  /// Basta **uma** das permissões, e há sempre pelo menos uma: toda tela do catálogo tem porta. A
  /// única tela sem porta é Início, e ela está fora daqui.
  requires: readonly [PermissionKey, ...PermissionKey[]];
  /// Onde a tela mora na navegação. Operação é o menu lateral; Configurações é a casca da
  /// engrenagem (ADR 0041). A posição não muda o dono: a tela continua resolvendo para um módulo,
  /// e contratação e permissão continuam as dele.
  placement: 'operation' | 'settings';
  /// O assunto que agrupa a tela no menu, quando ele não é o módulo dono. O Catálogo junta o que a
  /// empresa vende — serviço, produto, pacote e promoção —, que mora em três módulos (ADR 0055).
  /// Só muda o menu: dono, contratação e permissão continuam os do módulo.
  menuGroup?: CompanyMenuGroup;
};

export type CompanyMenuGroup = 'catalog' | 'team';

/// O nome de cada assunto do menu que não é um módulo.
export const companyMenuGroupLabels: Readonly<Record<CompanyMenuGroup, string>> = Object.freeze({
  catalog: 'Catálogo',
  team: 'Profissionais',
});

export const companyScreens: readonly CompanyScreen[] = Object.freeze([
  /// A ordem do catálogo é a ordem do menu, e ela é do sistema, igual para toda empresa e unidade:
  /// primeiro o que a recepção e quem atende usam o dia inteiro — agenda, cliente, cobrança,
  /// retirada, aplicação —, depois a gestão da casa e, por fim, suporte e o que é só dos sócios.
  /// O grupo aparece na posição do seu primeiro item.
  { key: 'scheduling', label: 'Agenda', requires: ['scheduling.read'], placement: 'operation' },
  { key: 'clients', label: 'Clientes', requires: ['clients.read'], placement: 'operation' },
  { key: 'sales', label: 'Vendas', requires: ['sales.read'], placement: 'operation' },
  {
    key: 'commissions',
    label: 'Comissões',
    requires: ['sales.commissions.read', 'sales.commissions.read_own'],
    placement: 'operation',
  },
  {
    key: 'prepaid-withdrawals',
    label: 'Retiradas de produtos',
    requires: ['prepaid_balances.read'],
    placement: 'operation',
  },
  /// Tela da operação, não do catálogo: quem atende registra aqui o protocolo do atendimento. Só
  /// aparece com a feature de aplicação contratada, porque é ela que dona a permissão. Vem antes
  /// do cadastro de protocolos porque é o que se abre todo dia.
  {
    key: 'protocol-applications',
    label: 'Aplicações de protocolo',
    requires: ['protocols.applications.read'],
    placement: 'operation',
  },
  { key: 'protocols', label: 'Protocolos', requires: ['protocols.read'], placement: 'operation' },
  {
    key: 'finance',
    label: 'Caixa e extrato',
    requires: ['financial_movements.read'],
    placement: 'operation',
  },
  /// Despesas e Receitas são o mesmo compromisso financeiro visto por sentido: cada tela fixa um
  /// lado, para quem paga não filtrar o que recebe e "conta" ficar só para conta bancária e plano.
  {
    key: 'payables',
    label: 'Despesas',
    requires: ['financial_obligations.read'],
    placement: 'operation',
  },
  {
    key: 'receivables',
    label: 'Receitas',
    requires: ['financial_obligations.read'],
    placement: 'operation',
  },
  {
    key: 'payment-reports',
    label: 'Operadoras',
    requires: ['financial_reports.read'],
    placement: 'operation',
  },
  {
    key: 'classification',
    label: 'Classificar lançamentos',
    requires: ['accounting.classify'],
    placement: 'operation',
  },
  {
    key: 'classification-rules',
    label: 'Regras de classificação',
    requires: ['accounting.rules.manage'],
    placement: 'operation',
  },
  {
    key: 'chart-of-accounts',
    label: 'Plano de contas',
    requires: ['accounting.chart.read'],
    placement: 'operation',
  },
  {
    key: 'dre',
    label: 'DRE',
    requires: ['accounting.reports.read'],
    placement: 'operation',
  },
  {
    key: 'cash-flow',
    label: 'Fluxo de caixa',
    requires: ['accounting.reports.read'],
    placement: 'operation',
  },
  {
    key: 'accounting-closing',
    label: 'Fechamento',
    requires: ['accounting.reports.read'],
    placement: 'operation',
  },
  /// O cadastro de quem trabalha na unidade: é da Agenda, mas não é agenda (ADR 0083). O assunto
  /// próprio o mantém como item direto no menu, em vez de virar filho de um grupo Agenda.
  {
    key: 'professionals',
    label: 'Profissionais',
    requires: ['scheduling.read'],
    placement: 'operation',
    menuGroup: 'team',
  },
  {
    key: 'services',
    label: 'Serviços',
    requires: ['services.read'],
    placement: 'operation',
    menuGroup: 'catalog',
  },
  {
    key: 'products',
    label: 'Produtos',
    requires: ['inventory.read'],
    placement: 'operation',
    menuGroup: 'catalog',
  },
  {
    key: 'packages',
    label: 'Pacotes',
    requires: ['sales.read'],
    placement: 'operation',
    menuGroup: 'catalog',
  },
  {
    key: 'promotions',
    label: 'Promoções',
    requires: ['sales.read'],
    placement: 'operation',
    menuGroup: 'catalog',
  },
  { key: 'inventory', label: 'Estoque', requires: ['inventory.read'], placement: 'operation' },
  /// Telas da feature de Consumo de materiais, agrupadas no módulo Estoque (ADR 0045).
  {
    key: 'material-usage',
    label: 'Consumo de materiais',
    requires: ['materials_usage.read'],
    placement: 'operation',
  },
  {
    key: 'material-sufficiency',
    label: 'Suficiência de materiais',
    requires: ['materials_usage.read'],
    placement: 'operation',
  },
  /// Vizinha do Estoque, e não dentro dele: Compras é módulo próprio e também contrata serviço,
  /// que não entra em estoque nenhum.
  { key: 'purchasing', label: 'Compras', requires: ['purchasing.read'], placement: 'operation' },
  /// O que já saiu é operação e histórico de entrega; o texto que vai sair é configuração e mora
  /// na engrenagem (ADR 0041, decisão 5). A chave continua `communication` para os atalhos que já
  /// apontam para ela.
  {
    key: 'communication',
    label: 'Caixa de saída',
    requires: ['email_messages.read'],
    placement: 'operation',
  },
  { key: 'partners', label: 'Sócios', requires: ['partners.read'], placement: 'operation' },
  {
    key: 'contributions',
    label: 'Aportes',
    requires: ['investment.read'],
    placement: 'operation',
  },
  { key: 'entries', label: 'Lançamentos', requires: ['investment.read'], placement: 'operation' },
  { key: 'metrics', label: 'Indicadores', requires: ['investment.read'], placement: 'operation' },
  {
    key: 'profile',
    label: 'Dados da empresa',
    requires: ['company_profile.read'],
    placement: 'settings',
  },
  { key: 'units', label: 'Unidades', requires: ['business_units.read'], placement: 'settings' },
  { key: 'access', label: 'Pessoas e acessos', requires: ['users.read'], placement: 'settings' },
  /// As duas telas de montar o Prontuário. A ficha da cliente não é tela de menu: mora na página
  /// do cliente, na aba Prontuário (ADR 0038).
  {
    key: 'record-templates',
    label: 'Modelos de ficha',
    requires: ['records.templates.manage'],
    placement: 'settings',
  },
  {
    key: 'health-themes',
    label: 'Temas de saúde',
    requires: ['records.templates.manage'],
    placement: 'settings',
  },
  {
    key: 'email-templates',
    label: 'Modelos de e-mail',
    requires: ['email_templates.read'],
    placement: 'settings',
  },
  {
    key: 'whatsapp-templates',
    label: 'Mensagens de WhatsApp',
    requires: ['whatsapp_templates.read'],
    placement: 'settings',
  },
  {
    key: 'scheduling-settings',
    label: 'Sinal do agendamento',
    requires: ['scheduling.settings.manage'],
    placement: 'settings',
  },
  // O que aparece da empresa na vitrine pública, separado do sinal (ADR 0067, decisão 12).
  {
    key: 'customer-portal-settings',
    label: 'Portal de clientes',
    requires: ['scheduling.settings.manage'],
    placement: 'settings',
  },
  {
    key: 'card-settings',
    label: 'Cartão e taxas',
    requires: ['financial_cards.settings.manage'],
    placement: 'settings',
  },
  {
    key: 'sales-settings',
    label: 'Comprovantes de pagamento',
    requires: ['sales.settings.manage'],
    placement: 'settings',
  },
  {
    key: 'commission-rules',
    label: 'Regras de comissão',
    requires: ['sales.commissions.manage'],
    placement: 'settings',
  },
  {
    key: 'online-payments-settings',
    label: 'Cobrança online',
    requires: ['online_payments.settings.manage'],
    placement: 'settings',
  },
  {
    key: 'prepaid-settings',
    label: 'Saldos pré-pagos',
    requires: ['prepaid_balances.settings.manage'],
    placement: 'settings',
  },
  { key: 'audit', label: 'Auditoria', requires: ['audit.read'], placement: 'settings' },
]);

const byKey = new Map(companyScreens.map((screen) => [screen.key, screen]));

export function findCompanyScreen(key: string): CompanyScreen | undefined {
  return byKey.get(key);
}

/// O módulo comercial dono da tela nasce das permissões que a abrem. Uma tela não pode misturar
/// módulos: o menu deixaria de saber sob qual produto agrupá-la e o desligamento ficaria ambíguo.
export function resolveCompanyScreenModule(
  screen: CompanyScreen,
  permissions: readonly PermissionDefinition[] = permissionCatalog,
  modules: readonly ModuleDefinition[] = moduleCatalog,
): ModuleDefinition {
  const permissionByKey = new Map(permissions.map((item) => [item.key, item]));
  const owners = new Set(
    screen.requires.map((key) => {
      const permission = permissionByKey.get(key);
      if (!permission)
        throw new Error(`Tela "${screen.key}" usa a permissão inexistente "${key}".`);
      return permission.moduleKey;
    }),
  );
  if (owners.size !== 1) {
    throw new Error(`Tela "${screen.key}" resolve para mais de um módulo.`);
  }
  const [ownerKey] = owners;
  const owner = ownerKey ? modules.find((item) => item.key === ownerKey) : undefined;
  if (!owner)
    throw new Error(`Tela "${screen.key}" resolve para o módulo inexistente "${ownerKey}".`);
  return owner;
}

export const moduleForCompanyScreen = resolveCompanyScreenModule;

for (const screen of companyScreens) resolveCompanyScreenModule(screen);

/// Se esta pessoa alcança a tela. O root opera a plataforma e dá suporte: alcança todas.
///
/// Chave fora do catálogo responde "não". Guardar um atalho que ninguém sabe o que é seria guardar
/// lixo com aparência de configuração — e é assim que uma tela removida continua aparecendo no
/// início de quem a favoritou.
export function canReachScreen(
  key: string,
  actor: { isRoot: boolean; permissions: readonly string[] },
): boolean {
  const screen = byKey.get(key);
  if (!screen) return false;
  if (actor.isRoot) return true;
  return screen.requires.some((permission) => actor.permissions.includes(permission));
}
